import { FrameNotFound } from '@Components/errorHandling';
import { AUDITING_API } from '@Constants/backendConstants';
import { getBadgeVariantByCategory, getEnumActionEventCategoryLabel, type ApiActionEventLog } from '@Model';
import { json as jsonLang } from '@codemirror/lang-json';
import {
    Card,
    DateInputBase,
    EdgeSpacer,
    FlexColumn,
    FrameHeader,
    Grid,
    GridColumns,
    isNullOrEmpty,
    LoadingFrame,
    ScrollContainer,
    TextInputBase,
    useFrameEditBaseNavigation,
    useServerObject,
} from '@synqo/frontend-component-library';
import CodeMirror from '@uiw/react-codemirror';
import { useMemo } from 'react';
import { useParams } from 'react-router';

export interface FrameEditActionEventLogProps {
    /**
     * When set, this id is used instead of the route param.
     * Enables opening the frame inside an on-the-fly modal from other views.
     */
    primaryKey?: string;
    /**
     * When true the header's back button is hidden (or delegates to onClose).
     */
    embedded?: boolean;
    /**
     * Called when the header back button is used in an embedded context.
     * Only relevant when {@link embedded} is true.
     */
    onClose?: () => void;
}

export function FrameEditActionEventLog(props: FrameEditActionEventLogProps = {}) {
    const { itemId } = useParams();
    const primaryKey = props.primaryKey ?? itemId ?? '';

    const { result, isSuccess } = useServerObject<ApiActionEventLog>({
        apiResourcePath: `${AUDITING_API}/action_event_logs`,
        primaryKey,
        include: ['all'],
    });

    if (isSuccess === null) {
        return <LoadingFrame />;
    }
    if (!isSuccess) {
        return (
            <FrameNotFound
                apiResourcePath={`${AUDITING_API}/action_event_logs`}
                primaryKey={primaryKey}
                result={result}
                embedded={props.embedded ?? false}
            />
        );
    }

    return <FrameEditActionEventLogBody data={result.data} embedded={props.embedded} onClose={props.onClose} />;
}

interface FrameEditActionEventLogBodyProps {
    data: ApiActionEventLog;
    embedded?: boolean;
    onClose?: () => void;
}

const lockedOnChange = () => false;

function FrameEditActionEventLogBody({ data, embedded, onClose }: FrameEditActionEventLogBodyProps) {
    const { onBack: routeBack } = useFrameEditBaseNavigation();
    const onBack = embedded ? onClose : routeBack;

    const jsonExtensions = useMemo(() => [jsonLang()], []);

    return (
        <FlexColumn gap="none" height="100%" width="100%">
            <FrameHeader label="Action Log" resourceLabel="Action Log" onBack={onBack} />
            <ScrollContainer width="100%" height="100%">
                <FlexColumn gap="sm" className="mx-4">
                    <Card
                        name="HeadData"
                        label="Aktion"
                        badges={[
                            {
                                children: getEnumActionEventCategoryLabel(data.category),
                                variant: getBadgeVariantByCategory(data.category),
                            },
                        ]}
                    >
                        <Grid columns={12}>
                            <GridColumns spans={[3, 5, 4]}>
                                <DateInputBase
                                    name="created_at"
                                    label="Zeitpunkt"
                                    value={data.created_at}
                                    onChange={lockedOnChange}
                                    locked
                                />
                                <TextInputBase
                                    name="created_by"
                                    label="Benutzer ID"
                                    value={data.created_by}
                                    onChange={lockedOnChange}
                                    locked
                                />
                                <TextInputBase
                                    name="action"
                                    label="Aktion"
                                    value={data.action}
                                    onChange={lockedOnChange}
                                    locked
                                />
                            </GridColumns>
                        </Grid>
                    </Card>
                    <Card name="RelatedObject" label="Referenz">
                        <Grid columns={12}>
                            <GridColumns spans={[3, 5, 4]}>
                                <TextInputBase
                                    name="related_object_resource_name"
                                    label="Ressourcentyp"
                                    value={data.related_object_resource_name}
                                    onChange={lockedOnChange}
                                    locked
                                />
                                <TextInputBase
                                    name="related_object_display_string"
                                    label="Bezugsobjekt"
                                    value={data.related_object_display_string}
                                    onChange={lockedOnChange}
                                    locked
                                />
                                <TextInputBase
                                    name="related_object_id"
                                    label="Objekt-ID"
                                    value={data.related_object_id ?? ''}
                                    onChange={lockedOnChange}
                                    locked
                                />
                            </GridColumns>
                        </Grid>
                    </Card>
                    <Card name="Tracing" label="Tracing" collapsable={false}>
                        <Grid columns={12}>
                            <GridColumns spans={[6, 6]}>
                                <TextInputBase
                                    name="trace_id"
                                    label="Trace ID"
                                    value={data.trace_id}
                                    onChange={lockedOnChange}
                                    locked
                                />
                                <TextInputBase
                                    name="span_id"
                                    label="Span ID"
                                    value={data.span_id}
                                    onChange={lockedOnChange}
                                    locked
                                />
                            </GridColumns>
                        </Grid>
                    </Card>

                    {!isNullOrEmpty(data.remarks) && (
                        <Card name="Remarks" label="Bemerkungen" closed>
                            <TextInputBase
                                name="remarks"
                                label="Bemerkung"
                                value={data.remarks}
                                inputSize={5}
                                onChange={lockedOnChange}
                                locked
                            />
                        </Card>
                    )}

                    {!isNullOrEmpty(data.additional_data) && (
                        <Card name="AdditionalData" label="Zusätzliche Daten">
                            <CodeMirror
                                value={data.additional_data}
                                height="300px"
                                extensions={jsonExtensions}
                                editable={false}
                            />
                        </Card>
                    )}
                </FlexColumn>
                <EdgeSpacer />
            </ScrollContainer>
        </FlexColumn>
    );
}
